import type { TelemetryPayloadFields } from "./liveFrames";

/**
 * Guards and derivations for raw telemetry scalars.
 *
 * Ported from the crew app's `telemetryReadings` / `telemetryProblems` so all
 * three clients read the same wire the same way. Pure, so the rules can be
 * checked without a socket.
 */

/**
 * A usable temperature in °C, or null.
 *
 * Protobuf sends 0 for a scalar the firmware never populated, so the wire can't
 * distinguish "no sensor" from "zero" — the temperature fields sit in an
 * unconditional `data.update()` in `converters.py`. For temperature that
 * ambiguity resolves: a running mower is not at 0 °C, so zero means no reading
 * and must show as a dash rather than a suspiciously cold component.
 */
export function tempReading(c: number | null | undefined): number | null {
  return typeof c === "number" && c !== 0 ? c : null;
}

/** °C → °F. The robot reports Celsius and nothing rescales it in the pipeline,
 *  so the conversion belongs here at the display edge. Thresholds stay in
 *  Celsius and compare the raw reading — converting both sides would be two
 *  places to get wrong. */
export function toFahrenheit(c: number): number {
  return c * 1.8 + 32;
}

/** Worth noticing / needs attention, in °C. */
export const TEMP_WARM_C = 70;
export const TEMP_HOT_C = 85;

/**
 * The device reports a battery SOC of 300 (an out-of-range sentinel) when it
 * couldn't read the pack. Anything outside 0–100 means "no real reading", so
 * the UI must show a dash rather than a nonsense percentage.
 */
export function isValidSoc(pct?: number | null): pct is number {
  return typeof pct === "number" && pct >= 0 && pct <= 100;
}

/** What the UI says when the pack isn't reporting a usable SOC. */
export const SOC_UNREADABLE_LABEL = "Disconnected";

/**
 * Battery SOC as display text: "84%", or "Disconnected" when the reading is
 * the 300 sentinel (or anything else outside 0-100, or missing entirely).
 * Every surface that shows a percentage should go through this — rendering the
 * raw number put "300%" in the fleet table.
 */
export function socLabel(pct?: number | null): string {
  return isValidSoc(pct) ? `${Math.round(pct)}%` : SOC_UNREADABLE_LABEL;
}

/** Blade RPM at or above this counts as actively cutting. */
export const BLADE_ACTIVE_RPM = 50;

export function bladesOn(t: TelemetryPayloadFields): boolean | null {
  if (typeof t.blade_toggle_status === "boolean") return t.blade_toggle_status;
  if (typeof t.blade_rpm === "number") return t.blade_rpm >= BLADE_ACTIVE_RPM;
  return null;
}

/**
 * The strings firmware uses to say "this is fine". These are *cause* fields, so
 * the healthy value is a sentence, not an empty one: `pause_cause` reports "not
 * in pause" while running and the motor fault fields report "No error".
 * Treating those as problems puts a permanent red warning on the card, which is
 * worse than no warning — it trains people to ignore the section that exists to
 * get their attention.
 *
 * Deliberately an explicit list, not a pattern. An unrecognised value shows
 * through as a problem: a spurious warning gets reported and added here, while a
 * real fault swallowed by an over-eager regex does not.
 */
const HEALTHY_SENTINELS = new Set([
  "none",
  "no error",
  "no errors",
  "no fault",
  "no faults",
  "not in pause",
  "ok",
  "normal",
]);

/**
 * Values carrying no information either way — the firmware saying "I don't
 * know", which is neither "I am fine" nor "I have faulted". Separate from the
 * healthy set because they mean different things: a mower mowing normally
 * reports `ext_estop_cause: "unknown"`, and rendering that as an E-stop
 * contradicts every other field on the frame.
 */
const UNINFORMATIVE_SENTINELS = new Set([
  "unknown",
  "unavailable",
  "n/a",
  "na",
  "-",
  "--",
  "?",
]);

/**
 * Render any scalar as display text.
 *
 * The telemetry payload is the robot's frame verbatim and arrives untyped, so
 * a field's declared type is a claim about the current firmware, not a
 * guarantee about the bytes. Calling `.trim()` on whatever shows up crashed the
 * whole session view once already (`gps_status` is an int32 enum, not a label),
 * so coerce here instead of trusting the type at each call site.
 */
export function text(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v.trim();
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "";
}

/** The stop and fault reasons present on a reading, most-urgent first. Absent,
 *  blank, healthy and uninformative values all mean "no problem". */
export function telemetryProblems(t: TelemetryPayloadFields): string[] {
  const out: string[] = [];
  const add = (label: string, v: unknown) => {
    const s = text(v);
    if (!s) return;
    // Normalise how firmware varies: case, internal spacing, trailing dot.
    const key = s.toLowerCase().replace(/\s+/g, " ").replace(/\.$/, "");
    if (HEALTHY_SENTINELS.has(key) || UNINFORMATIVE_SENTINELS.has(key)) return;
    out.push(`${label}: ${s}`);
  };
  add("E-stop", t.ext_estop_cause);
  add("Paused", t.pause_cause);
  add("Pause conditions", t.active_pause_conditions);
  add("Event", t.event_cause);
  add("Left motor", t.left_motor_faults);
  add("Right motor", t.right_motor_faults);
  // Boolean rather than a cause string, so it carries no colon-prefixed detail.
  if (t.wheel_slip_detected === true) out.push("Wheel slip detected");
  return out;
}
